// Транспортный слой каталога: только запросы, без состояния и без React.
//
// Отдельный файл от хуков намеренно (app.md §5): функции здесь тестируются и
// переиспользуются вне React — в частности, из очереди синхронизации (T-007),
// которая работает без компонентов.

import { httpClient, type QueryParamValue } from '@/shared/api';

import type {
  AddFromCatalogResponse,
  Category,
  DeleteMasterProductResponse,
  LocationProduct,
  LocationSummary,
  MasterProduct,
  Paginated,
  ProductType,
  ShelfLifeBand,
} from './types';

// Пути относительны env.apiBaseUrl, который УЖЕ содержит версию (`.../api/v1`).
// Повторить префикс здесь — значит получить `/api/v1/api/v1/...` и 404 на всём каталоге.
const CATALOG = '/catalog';

export type CatalogListFilters = {
  readonly search?: string;
  readonly status?: string;
  readonly product_type?: string;
  readonly category?: string;
  readonly scope?: 'global' | 'own';
  readonly page?: number;
};

/**
 * Фильтры каталога сети. С `location` сервер размечает строки для этой точки:
 * `menu_status` — есть ли эталон в её меню (B-042), `is_new` — новое ли это для неё
 * (T-045), и отдаёт новые первыми. Без точки разметки нет (`null` / `false`).
 */
export type MasterProductFilters = CatalogListFilters & {
  readonly location?: string;
  /** Только новые для точки позиции. Без `location` сервер отвечает 400. */
  readonly new_for_location?: boolean;
  /** Размер страницы; сервер режет его до 200 (`DefaultPagination.max_page_size`). */
  readonly page_size?: number;
};

export type LocationProductFilters = CatalogListFilters & {
  /** Точка обязательна: список меню без неё сервер не отдаёт (400). */
  readonly location: string;
  readonly source?: 'local' | 'catalog';
};

function toParams(filters: Record<string, unknown>): Record<string, QueryParamValue> {
  // Пустая строка поиска — это «фильтра нет», а не «искать пустоту». Без отсева
  // такой параметр уезжает в URL и ломает кэш-ключи TanStack Query: `?search=`
  // и отсутствие параметра дают разные ключи при одинаковом результате.
  const params: Record<string, QueryParamValue> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '') {
      continue;
    }
    params[key] = value as QueryParamValue;
  }
  return params;
}

export const catalogApi = {
  listCategories: (filters: CatalogListFilters = {}) =>
    httpClient.get<Paginated<Category>>(`${CATALOG}/categories/`, {
      searchParams: toParams(filters),
    }),

  createCategory: (body: Partial<Category>) =>
    httpClient.post<Category>(`${CATALOG}/categories/`, body),

  updateCategory: (id: string, body: Partial<Category>) =>
    httpClient.patch<Category>(`${CATALOG}/categories/${id}/`, body),

  deleteCategory: (id: string) => httpClient.delete<void>(`${CATALOG}/categories/${id}/`),

  listBands: () => httpClient.get<readonly ShelfLifeBand[]>(`${CATALOG}/categories/bands/`),

  listProductTypes: () => httpClient.get<Paginated<ProductType>>(`${CATALOG}/product-types/`),

  listMasterProducts: (filters: MasterProductFilters = {}) =>
    httpClient.get<Paginated<MasterProduct>>(`${CATALOG}/master-products/`, {
      searchParams: toParams(filters),
    }),

  createMasterProduct: (body: Partial<MasterProduct>) =>
    httpClient.post<MasterProduct>(`${CATALOG}/master-products/`, body),

  updateMasterProduct: (id: string, body: Partial<MasterProduct>) =>
    httpClient.patch<MasterProduct>(`${CATALOG}/master-products/${id}/`, body),

  /**
   * Удаляет эталон и возвращает число отвязанных копий.
   *
   * Ответ не пустой (не 204): сколько меню точек стали локальными — часть
   * результата, которую обязан увидеть админ сети.
   */
  deleteMasterProduct: (id: string) =>
    httpClient.delete<DeleteMasterProductResponse>(`${CATALOG}/master-products/${id}/`),

  listLocationProducts: (filters: LocationProductFilters) =>
    httpClient.get<Paginated<LocationProduct>>(`${CATALOG}/location-products/`, {
      searchParams: toParams(filters),
    }),

  createLocationProduct: (location: string, body: Partial<LocationProduct>) =>
    httpClient.post<LocationProduct>(`${CATALOG}/location-products/`, body, {
      searchParams: { location },
    }),

  updateLocationProduct: (id: string, body: Partial<LocationProduct>) =>
    httpClient.patch<LocationProduct>(`${CATALOG}/location-products/${id}/`, body),

  deleteLocationProduct: (id: string) =>
    httpClient.delete<void>(`${CATALOG}/location-products/${id}/`),

  addFromCatalog: (location: string, masterProductIds: readonly string[]) =>
    httpClient.post<AddFromCatalogResponse>(
      `${CATALOG}/location-products/add-from-catalog/`,
      { master_product_ids: masterProductIds },
      { searchParams: { location } },
    ),

  listLocations: () => httpClient.get<Paginated<LocationSummary>>('/locations/'),
} as const;
